import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IImageTemplate } from '../../../models/ImageTemplate';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { invalidateAdminImageTemplateBySystemTemplate } from './useAdminImageTemplateBySystemTemplateQuery';
import { ADMIN_IMAGE_TEMPLATE_UPDATE_MUTATION_KEY } from './useIsAdminImageTemplateUpdating';

interface IParams {
	systemTemplateId?: string;
	templateId: string;
}

export function useAdminReingestImageTemplateMutation({
	onSuccess,
}: {
	onSuccess?: (imageTemplate: IImageTemplate) => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation<IImageTemplate, Api.IOperationResultNoValue, IParams>({
		mutationFn: ({ templateId }) =>
			userSession.webServiceHelper.callAsync<IImageTemplate>({
				path: `admin/imageTemplates/${encodeURIComponent(templateId)}/reingest`,
				method: 'POST',
			}),
		mutationKey: ADMIN_IMAGE_TEMPLATE_UPDATE_MUTATION_KEY,
		onError: error => toast.error(error?.systemMessage || "The update didn't finish. Try again."),
		// Also on failure: the API can re-ingest the template and then fail to refresh its images.
		onSettled: (_imageTemplate, _error, { systemTemplateId }) => {
			if (systemTemplateId) {
				invalidateAdminImageTemplateBySystemTemplate(systemTemplateId);
			}
		},
		onSuccess: imageTemplate => onSuccess?.(imageTemplate),
	});
}
